import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import axios from "axios";
import { categoryLabel, categoryPages, CategoryRecord as Category } from "../seo/categoryPages";
export { categoryLabel };
/** The category list, shared (one request) by the picker and the category page. */
export function useCategories() {
  return useQuery<Category[]>(
    ["shop-category-navigation"],
    async () => {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/category/get-all`
      );
      return Array.isArray(response.data?.data) ? response.data.data : [];
    },
    { staleTime: 300000, retry: false }
  );
}
export default function ShopCategories() {
  const [selected, setSelected] = useState("");
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useCategories();
  const slugs = categoryPages(data || []);
  const slugFor = (id: string) => slugs.find((c) => c.id === id)?.slug || id;
  return (
    <section
      id="browse-categories"
      className="shop-categories"
      aria-labelledby="shop-categories-title"
    >
      <div className="shop-categories-heading">
        <h3 id="shop-categories-title">Browse by category</h3>
        <p>Find the right parts faster.</p>
      </div>
      {isLoading ? (
        <p role="status">Loading categories…</p>
      ) : isError ? (
        <p>
          We couldn’t load the categories.{" "}
          <button onClick={() => refetch()}>Try again</button> or browse the
          products below.
        </p>
      ) : !data?.length ? (
        <p>Browse or search the collection below.</p>
      ) : (
        <form
          className="shop-category-picker"
          onSubmit={(event) => {
            event.preventDefault();
            navigate(
              selected ? `/shop/${encodeURIComponent(slugFor(selected))}` : "/products"
            );
          }}
        >
          <label className="sr-only" htmlFor="shop-category-select">
            Choose a product category
          </label>
          <select
            id="shop-category-select"
            value={selected}
            onChange={(event) => setSelected(event.target.value)}
          >
            <option value="">All products</option>
            {data.map((category) =>
              category.subcategory?.length ? (
                <optgroup
                  key={category._id}
                  label={categoryLabel(category.name)}
                >
                  <option value={category._id}>
                    All {categoryLabel(category.name)}
                  </option>
                  {category.subcategory.map((sub) => (
                    <option key={sub._id} value={sub._id}>
                      {categoryLabel(sub.name)}
                    </option>
                  ))}
                </optgroup>
              ) : (
                <option key={category._id} value={category._id}>
                  {categoryLabel(category.name)}
                </option>
              )
            )}
          </select>
          <button type="submit">
            View products <span aria-hidden="true">→</span>
          </button>
        </form>
      )}
      {/* Plain links as well as the picker, so search engines can find every category. */}
      {!!data?.length && (
        <nav className="shop-category-links" aria-label="Product categories">
          {slugs.filter((c) => !c.parentLabel).map((c) => <Link key={c.id} to={`/shop/${c.slug}`}>{c.label}</Link>)}
        </nav>
      )}
    </section>
  );
}
